import { jsPDF } from 'jspdf';
import { API_BASE_URL } from 'src/apiConfig';

const getTemplateImageUrl = (templateId) =>
  `${API_BASE_URL}/api/diplomaTemplates.php?action=get_image&id=${templateId}`;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });

const createWorkerStudentRecord = (student, award, diplomaId, template_type, certId) => ({
  'Ime i prezime': student.name || '',
  'Разред': String(student.class || ''),
  'class': String(student.class || ''),
  'Назив школе': student.school_name || student.schoolName || '',
  'school': student.school_name || student.schoolName || '',
  'Матична школа': student.maticna_skola || '',
  'Место': student.location_name || student.city_name || '',
  'location': student.location_name || student.city_name || '',
  'Награда': award || '',
  'award': award || '',
  'Поени': String(student.point_sum ?? '').replace('.', ','),
  'points': String(student.point_sum ?? '').replace('.', ','),
  '_diplomaId': diplomaId || '0',
  '_templateType': String(template_type) || '',
  '_certId': certId,
});

const runWorkerForSingleStudent = async (student, templateId, award, diplomaId, templateType, certId = '') => {
  const templateUrl = getTemplateImageUrl(templateId);
  let templateImg = null;

  try {
    templateImg = await loadImage(templateUrl);
  } catch (error) {
    console.warn('Template image failed to load for single diploma regeneration:', error);
  }

  const bitmap = templateImg ? await createImageBitmap(templateImg) : null;
  const worker = new Worker(new URL('../workers/diplomaWorker.js', import.meta.url));
  const payload = createWorkerStudentRecord(student, award, diplomaId, templateType, certId);

  const result = await new Promise((resolve, reject) => {
    const listener = (e) => {
      if (e.data?.id === 1) {
        worker.removeEventListener('message', listener);
        resolve(e.data.results[0]);
      }
    };
    worker.addEventListener('message', listener);
    worker.onerror = reject;
    worker.postMessage({
      id: 1,
      students: [payload],
      templateBitmap: bitmap,
      templateWidth: templateImg?.width || 1024,
      templateHeight: templateImg?.height || 768,
    });
  });

  worker.terminate();
  return result;
};

const createPdfBlobFromResult = async (result) => {
  const blob = new Blob([result.buffer], { type: 'image/jpeg' });
  const bitmap = await createImageBitmap(blob);
  const imgAspectRatio = bitmap.width / bitmap.height;
  const a4AspectRatio = 297 / 210;

  const orientation = imgAspectRatio > a4AspectRatio ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageAspect = pageWidth / pageHeight;

  let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
  if (imgAspectRatio > pageAspect) {
    drawWidth = pageWidth;
    drawHeight = pageWidth / imgAspectRatio;
    offsetY = (pageHeight - drawHeight) / 2;
  } else {
    drawHeight = pageHeight;
    drawWidth = pageHeight * imgAspectRatio;
    offsetX = (pageWidth - drawWidth) / 2;
  }

  const imageUrl = URL.createObjectURL(blob);
  pdf.addImage(imageUrl, 'JPEG', offsetX, offsetY, drawWidth, drawHeight);
  URL.revokeObjectURL(imageUrl);

  return pdf.output('blob');
};

export const regenerateSingleDiploma = async (studentId) => {
  const response = await fetch(
    `${API_BASE_URL}/api/diplomas.php?action=regenerateSingle&studentId=${encodeURIComponent(studentId)}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch regenerate data: ${response.status}`);
  }

  const responseJson = await response.json();
  if (responseJson.error) throw new Error(responseJson.error);

  const payload = responseJson.data;

  const diplomaList = payload.diplomas ?? [
    {
      templateId: payload.templateId,
      templateType: payload.templateType,
      schoolId: payload.schoolId,
      award: payload.award,
      diplomaId: payload.diplomaId,
      schoolName: payload.schoolName,
    },
  ];

  const { student } = payload;
  if (!student) throw new Error('Incomplete regeneration data returned from backend');

  const uploadResults = [];

  for (const diploma of diplomaList) {
    const { templateId, templateType, award, diplomaId, certId } = diploma;

    if (!templateId || !award) {
      console.warn('Skipping diploma entry with missing templateId or award:', diploma);
      continue;
    }

    const workerResult = await runWorkerForSingleStudent(
      student, templateId, award, diplomaId, templateType, certId
    );
    const pdfBlob = await createPdfBlobFromResult(workerResult);

    const formData = new FormData();
    formData.append('pdf', pdfBlob, `${student.name || 'diploma'}.pdf`);
    formData.append('studentId', student.id);
    formData.append(
      'schoolId',
      student.schoolId || student.school_id || student.schoolid || ''
    );
    formData.append('award', award);
    formData.append('templateId', templateId);
    formData.append('diplomaId', diplomaId || '');

    const uploadResponse = await fetch(
      `${API_BASE_URL}/api/diplomas.php?action=uploadPdf&regenerate=1`,
      { method: 'POST', body: formData }
    );

    const contentType = uploadResponse.headers.get('Content-Type') || '';
    if (!contentType.includes('application/json')) {
      const rawText = await uploadResponse.text();
      console.error('Non-JSON upload response:', rawText);
      throw new Error('Upload returned non-JSON. Check PHP logs.');
    }

    const uploadData = await uploadResponse.json();
    if (!uploadResponse.ok || uploadData.error) {
      throw new Error(uploadData.error || 'Failed to upload regenerated diploma');
    }

    uploadResults.push(uploadData);
  }

  return uploadResults[uploadResults.length - 1] ?? { success: true };
};